import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { Button } from '@/components/ui/button.tsx';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Alert, AlertDescription } from '@/components/ui/alert.tsx';
import { ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { useResetPasswordMutation } from '@/features/auth/hooks/mutations/reset-password.ts';
import { EyePasswordShow } from '@/assets/auth/eye-password-show.tsx';
import { EyePasswordHide } from '@/assets/auth/eye-password-hide.tsx';
import { LoadingSpinner } from '@/components/ui/loading-spinner.tsx';
import { useTranslation } from 'react-i18next';
import { PasswordRequirements } from '@/features/auth/components/password-requirements.tsx';
import { useAuthStore } from '@/services/store/use-auth-store.tsx';
import { useShallow } from 'zustand/react/shallow';
import Logo from '@tenant/logo';

const passwordValidation = new RegExp(/^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9]).{8,}$/);

const getResetPasswordSchema = (translation?: {
  codeRequired: string;
  codeMissing: string;
  codeNumber: string;
  codePositive: string;
  emailRequired: string;
  emailValid: string;
  passwordRequired: string;
  passwordComplexity: string;
  passwordsDoNotMatch: string;
}) => {
  const messages = translation ?? {
    codeRequired: 'Code is required',
    codeMissing: 'Code cannot be empty',
    codeNumber: 'Code must be a number',
    codePositive: 'Code must be a positive number',
    emailRequired: 'Email is required',
    emailValid: 'Please enter a valid email',
    passwordRequired: 'Password is required',
    passwordComplexity: 'Password does not meet complexity requirements',
    passwordsDoNotMatch: 'Passwords do not match',
  };

  return z
    .object({
      code: z
        .string({
          error: messages.codeRequired,
        })
        .min(1, {
          message: messages.codeMissing,
        })
        .refine((string) => !isNaN(Number(string)), messages.codeNumber)
        .refine((string) => Number(string) >= 0, messages.codePositive),
      email: z
        .string({
          error: messages.emailRequired,
        })
        .min(1, messages.emailRequired)
        .email(messages.emailValid),
      password: z
        .string({
          error: messages.passwordRequired,
        })
        .regex(passwordValidation, {
          message: messages.passwordComplexity,
        }),
      confirmPassword: z.string({
        error: messages.passwordRequired,
      }),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: messages.passwordsDoNotMatch,
      path: ['confirmPassword'],
    });
};

export const ResetPassword: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { isAuthenticated } = useAuthStore(
    useShallow((state) => ({ isAuthenticated: state.isAuthenticated }))
  );

  const resetPasswordSchema = getResetPasswordSchema({
    codeRequired: t('auth.errors.codeRequired'),
    codeMissing: t('auth.errors.codeMissing'),
    codeNumber: t('auth.errors.codeNumber'),
    codePositive: t('auth.errors.codePositive'),
    emailRequired: t('auth.errors.emailRequired'),
    emailValid: t('auth.errors.emailValid'),
    passwordRequired: t('auth.errors.passwordRequired'),
    passwordComplexity: t('auth.errors.passwordComplexity'),
    passwordsDoNotMatch: t('auth.errors.passwordsDoNotMatch'),
  });

  const { email, code } = {
    email: searchParams.get('email') || '',
    code: searchParams.get('code') || '',
  };

  const form = useForm({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      email,
      code,
      password: '',
      confirmPassword: '',
    },
  });

  const resetPasswordMutation = useResetPasswordMutation({
    onSuccess: () => {
      form.reset();
      setTimeout(() => {
        navigate('/');
      }, 3000);

      form.setValue('confirmPassword', '');
    },
  });

  async function onSubmit(values: z.infer<typeof resetPasswordSchema>) {
    resetPasswordMutation.mutate({
      code: values.code,
      email: values.email,
      password: values.password,
    });
  }

  if (isAuthenticated || !code || !email) {
    return (
      <div className="min-h-[390px]">
        <Navigate to="/" replace={true} />
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col items-center justify-center gap-2">
        <Logo width="200" height="58" />
      </div>
      <div className="mt-8 flex max-w-[400px] flex-col gap-4">
        {resetPasswordMutation.isSuccess && (
          <Alert variant="success" className="flex items-center gap-4">
            <div className="h-4 w-4">
              <ExclamationTriangleIcon className="h-4 w-4" />
            </div>
            <AlertDescription>{t('auth.resetPassword.successMessage')}</AlertDescription>
          </Alert>
        )}
        {resetPasswordMutation.isError && (
          <Alert variant="destructive" className="flex items-center gap-4">
            <div className="h-4 w-4">
              <ExclamationTriangleIcon className="h-4 w-4" />
            </div>
            <AlertDescription>{resetPasswordMutation.error.message}</AlertDescription>
          </Alert>
        )}
        {(form.formState.errors.code?.message || form.formState.errors.email?.message) && (
          <Alert variant="destructive" className="flex items-center gap-4">
            <div className="h-4 w-4">
              <ExclamationTriangleIcon className="h-4 w-4" />
            </div>
            <AlertDescription>
              {form.formState.errors.code?.message ??
                form.formState.errors.email?.message ??
                ''}
            </AlertDescription>
          </Alert>
        )}
        <h2 className="text-2xl font-bold">{t('auth.resetPassword.title')}</h2>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor="password" className="required text-[16px]">
                    {t('auth.resetPassword.newPassword')}
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        {...field}
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        placeholder={t('auth.login.passwordPlaceholder')}
                        className="text-[16px]"
                        required
                        minLength={8}
                      />
                      <div
                        className="absolute top-1 right-4 cursor-pointer rounded-lg p-1 transition-colors hover:bg-[#e2e8f0]"
                        onClick={() => setShowPassword((s) => !s)}
                      >
                        {showPassword ? (
                          <EyePasswordShow width="19" height="19" />
                        ) : (
                          <EyePasswordHide width="19" height="19" />
                        )}
                      </div>
                    </div>
                  </FormControl>
                  <PasswordRequirements control={form.control} watch="password" />
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor="confirmPassword" className="required text-[16px]">
                    {t('auth.resetPassword.confirmPassword')}
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        {...field}
                        id="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        placeholder={t('auth.login.passwordPlaceholder')}
                        className="text-[16px]"
                        required
                        minLength={8}
                      />
                      <div
                        className="absolute top-1 right-4 cursor-pointer rounded-lg p-1 transition-colors hover:bg-[#e2e8f0]"
                        onClick={() => setShowConfirmPassword((s) => !s)}
                      >
                        {showConfirmPassword ? (
                          <EyePasswordShow width="19" height="19" />
                        ) : (
                          <EyePasswordHide width="19" height="19" />
                        )}
                      </div>
                    </div>
                  </FormControl>
                  <FormMessage>
                    {form.formState.errors.confirmPassword?.message && ''}
                  </FormMessage>
                </FormItem>
              )}
            />
            <Button
              disabled={resetPasswordMutation.isPending}
              type="submit"
              className="flex w-full items-center gap-2"
              size="lg"
            >
              {resetPasswordMutation.isPending && <LoadingSpinner size={24} />}
              <span className="text-[16px] select-none">
                {t('auth.resetPassword.resetButton')}
              </span>
            </Button>
          </form>
        </Form>
        <div className="flex justify-center gap-1 text-center">
          <Link to="/" className="link-underline">
            {t('auth.common.returnToLogin')}
          </Link>
        </div>
      </div>
    </div>
  );
};
